import React, { useEffect, useState } from 'react'
import { Drawer, IconButton } from '@mui/material'
import { ChevronRight, Close } from '@mui/icons-material'
import api from '../../../api'
import toast from 'react-hot-toast'
import { getLocalStorage, homeScreenRedirection, isSuccess, setLocalStorage, showError } from '../../../utils/helper'

const AVATAR_COLORS = [
  { bg: '#EEF2FF', text: '#456DB3' },
  { bg: '#FFF7ED', text: '#EA580C' },
  { bg: '#F0FDF4', text: '#16A34A' },
  { bg: '#FFF1F2', text: '#E11D48' },
  { bg: '#F5F3FF', text: '#7C3AED' },
]

const getAvatarColor = (name: string) => {
  const idx = (name?.charCodeAt(0) || 0) % AVATAR_COLORS.length
  return AVATAR_COLORS[idx]
}

type WorkspaceItem = {
  workspace_name: string
  workspace_type: string
  total_projects: number
  total_users: number
  subsidiary_logo: string
  subsidiary_master_id: string
  user_subsidiary_id: string
  user_master_id: string
  organization_name: string
}

type WorkspaceGroup = {
  organisation_name: string
  linked_subsidiaries: WorkspaceItem[]
}

type Props = {
  open: boolean
  onClose: () => void
}

const WorkspaceRow = ({
  workspace,
  isCurrent,
  isSwitching,
  disabled,
  onClick,
}: {
  workspace: WorkspaceItem
  isCurrent: boolean
  isSwitching: boolean
  disabled: boolean
  onClick: () => void
}) => {
  const [hovered, setHovered] = useState(false)
  const color = getAvatarColor(workspace.workspace_name || '')
  const initial = workspace.workspace_name?.[0]?.toUpperCase() || '?'
  const active = hovered && !isCurrent && !disabled

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={disabled}
      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all"
      style={{
        backgroundColor: isCurrent ? '#EEF2FF' : active ? '#F5F8FE' : undefined,
        boxShadow: active ? '0 4px 14px rgba(0,0,0,0.08)' : 'none',
        transform: active ? 'translateX(2px)' : 'none',
      }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold overflow-hidden"
        style={{ backgroundColor: color.bg, color: color.text }}
      >
        {workspace.subsidiary_logo ? <img src={workspace.subsidiary_logo} alt={workspace.workspace_name} className="w-full h-full object-cover" /> : initial}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">{workspace.workspace_name}</p>
        <p className="text-xs text-gray-400">
          {workspace.total_projects} projects · {workspace.total_users} users
        </p>
      </div>
      {isSwitching ? (
        <div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin flex-shrink-0" style={{ borderColor: '#456DB3', borderTopColor: 'transparent' }} />
      ) : !isCurrent ? (
        <div
          className="flex-shrink-0 flex items-center justify-center rounded-full transition-all"
          style={{ width: 32, height: 32, backgroundColor: active || isCurrent ? 'rgba(69,109,179,0.12)' : 'transparent' }}
        >
          <ChevronRight
            fontSize="small"
            className="transition-all"
            style={{ color: active || isCurrent ? '#456DB3' : '#9CA3AF', transform: active ? 'translateX(2px)' : 'none' }}
          />
        </div>
      ) : null}
    </button>
  )
}

const WorkspaceSwitcherDrawer = ({ open, onClose }: Props) => {
  const [groups, setGroups] = useState<WorkspaceGroup[]>([])
  const [fetching, setFetching] = useState(false)
  const [switching, setSwitching] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const currentSmi = getLocalStorage('smi')

  useEffect(() => {
    if (!open) return
    setSearch('')
    setFetching(true)
    api
      .getAfterAuthWorkspace()
      .then((res: any) => {
        setFetching(false)
        if (isSuccess(res)) {
          setGroups(
            Object.entries(res?.data?.data || {})
              .filter(([org]) => org.toLowerCase() !== 'acme')
              .map(([organisation_name, linked_subsidiaries]) => ({
                organisation_name,
                linked_subsidiaries: (linked_subsidiaries as WorkspaceItem[]).filter((w) => w.workspace_name?.toLowerCase() !== 'acme-global'),
              }))
              .filter(({ linked_subsidiaries }) => linked_subsidiaries.length > 0),
          )
        } else {
          toast.error(res?.data?.meta?.message)
        }
      })
      .catch((err: any) => {
        setFetching(false)
        showError(err)
      })
  }, [open])

  const handleSwitch = (workspace: WorkspaceItem) => {
    if (switching || workspace.subsidiary_master_id === currentSmi) return
    setSwitching(workspace.subsidiary_master_id)

    if (workspace.workspace_type === 'Freemium') {
      window.location.href = `https://demo-web-chat-portal-eu1-aws.stage.infra.navatechgroup.com?access_token=${getLocalStorage('access_token')}&refresh_token=${getLocalStorage('refresh_token')}`
      return
    }

    api
      .postChangeWorkspace({
        subsidiary_master_id: workspace.subsidiary_master_id,
        user_subsidiary_id: workspace.user_subsidiary_id,
        user_master_id: workspace.user_master_id,
      })
      .then((res: any) => {
        if (isSuccess(res)) {
          const { first_name, last_name, permission_details, profile_pic, theme_json, subsidiary_logo, subsidiary_master_id } = res?.data?.data as any
          const { access_token, refresh_token } = res?.data?.meta as any
          setLocalStorage('theme', typeof theme_json === 'string' ? theme_json : JSON.stringify(theme_json))
          setLocalStorage('logo', subsidiary_logo)
          setLocalStorage('permissions', JSON.stringify(permission_details))
          setLocalStorage('access_token', access_token)
          setLocalStorage('refresh_token', refresh_token)
          setLocalStorage('user_info', JSON.stringify({ firstName: first_name, lastName: last_name, profilePic: profile_pic }))
          setLocalStorage('smi', subsidiary_master_id)
          setLocalStorage('workspace_name', workspace.workspace_name)
          setLocalStorage('organization_name', workspace.organization_name || '')
          toast.success('Workspace changed successfully.')
          window.location.href = homeScreenRedirection(JSON.stringify(permission_details))
        } else {
          setSwitching(null)
          toast.error(res?.data?.meta?.message)
        }
      })
      .catch((err: any) => {
        setSwitching(null)
        showError(err)
      })
  }

  const allWorkspaces = groups.flatMap((g) => g.linked_subsidiaries)
  const currentWorkspace = allWorkspaces.find((w) => w.subsidiary_master_id === currentSmi)

  const filteredGroups = search.trim()
    ? groups
        .map((g) => ({
          ...g,
          linked_subsidiaries: g.linked_subsidiaries.filter((w) => w.workspace_name?.toLowerCase().includes(search.toLowerCase())),
        }))
        .filter((g) => g.linked_subsidiaries.length > 0)
    : groups

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 380, borderRadius: '16px 0 0 16px' } }}>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="text-lg font-bold text-gray-900">Select Subsidiary</span>
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </div>

        {/* Active workspace pill */}
        {!fetching && currentWorkspace && (
          <div className="mx-4 mt-4 px-4 py-3 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#EEF2FF' }}>
            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: '#456DB3' }}>
                {currentWorkspace.workspace_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{currentWorkspace.organization_name}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="px-4 mt-4">
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50">
            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search subsidiaries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-700 outline-none placeholder-gray-400"
            />
          </div>
        </div>

        {/* Workspace list */}
        <div className="flex-1 overflow-y-auto px-4 mt-4 pb-4 no-scrollbar">
          {fetching ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl animate-pulse">
                  <div className="w-10 h-10 rounded-xl bg-gray-200 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-2.5 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {filteredGroups.map((group) => (
                <div key={group.organisation_name} className="mb-5">
                  <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase mb-2 px-1">{group.organisation_name}</p>
                  <div className="space-y-1">
                    {group.linked_subsidiaries.map((ws) => (
                      <>
                        <WorkspaceRow
                          key={ws.subsidiary_master_id}
                          workspace={ws}
                          isCurrent={ws.subsidiary_master_id === currentSmi}
                          isSwitching={switching === ws.subsidiary_master_id}
                          disabled={!!switching}
                          onClick={() => handleSwitch(ws)}
                        />
                        <hr className="my-2 border-gray-200" />
                      </>
                    ))}
                  </div>
                </div>
              ))}
              {groups.length > 0 && filteredGroups.length === 0 && <p className="text-center text-sm text-gray-400 py-8">No workspaces match "{search}"</p>}
            </>
          )}
        </div>
      </div>
    </Drawer>
  )
}

export default WorkspaceSwitcherDrawer
